// src/App.tsx
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { CssBaseline, Box, CircularProgress } from '@mui/material';

// Eager: the routes a logged-out visitor can land on. Splitting these would only add a
// round trip to the one screen everybody sees first.
import Login from './pages/Login';
import Logout from './pages/Logout';
import ResetPassword from './components/authentication/ForgotPasswordContainer';
import Redirect from './pages/Redirect';
import Navbar from './components/Navbar';

// Split: every route behind a login, each its own chunk.
//
// These were all static imports, so the browser downloaded Billing, Reporting, Systems,
// Overlays, Layers and the asset viewer -- and through the viewer, three.js -- before it
// could paint the login form. A route nobody visits is now a chunk nobody fetches.
const Assets = React.lazy(() => import('./pages/Assets'));
const About = React.lazy(() => import('./pages/About'));
const Info = React.lazy(() => import('./pages/Info'));
const IFrame = React.lazy(() => import('./pages/IFrame'));
const AccountPage = React.lazy(() => import('./pages/Account'));
const Billing = React.lazy(() => import('./pages/Billing'));
const Reporting = React.lazy(() => import('./pages/Reporting'));
const AssetQueue = React.lazy(() => import('./pages/AssetQueue'));
const ClientDashboard = React.lazy(() => import('./pages/ClientDashboard'));
const CsvDownloadPage = React.lazy(() => import('./pages/CsvDownloadPage'));
const Systems = React.lazy(() => import('./pages/Systems'));
const Layers = React.lazy(() => import('./pages/Layers'));
const Overlays = React.lazy(() => import('./pages/Overlays'));
import { ThemeProvider } from './context/ThemeContext';
const AssetPage = React.lazy(() => import('./pages/Asset'));
import { SessionProvider } from './context/SessionContext';
import { RealtimeToastProvider } from './context/RealtimeToastContext';
import GlobalLoadingOverlay from './components/GlobalLoadingOverlay';

const AppContent: React.FC = () => {
  const location = useLocation();
  const isIframe = location.pathname.toLowerCase().includes('/iframe');

  return (
    <Box sx={{ width: "100vw", height: "100vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
     {!isIframe && <Navbar />}

      <GlobalLoadingOverlay />
      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        {/* One boundary for every split route. The chunks are small and same-origin, so this
            is a flash at most on a first visit and nothing on a warm cache. */}
        <Suspense
          fallback={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <CircularProgress />
            </Box>
          }
        >
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/logout" element={<Logout />} />
          <Route path="/resetPassword" element={<ResetPassword />} />

          {/* Iframe Routes - No authentication required */}
          <Route path="/iframe/:systemCode/:assetId" element={<IFrame />} />
          <Route path="/iframe/:systemCode/:assetId/:image_type" element={<IFrame />} />
          <Route path="/iframe/*" element={<Navigate to="/" replace />} />

          {/* Protected Routes */}
          <Route path="/" element={<Redirect />} />
          <Route path="/assets" element={<Assets />} />

          <Route path="/billing" element={<Billing />} />
          <Route path="/reporting" element={<Reporting />} />
          <Route path="/asset-queue" element={<AssetQueue />} />
          <Route path="/client-dashboard" element={<ClientDashboard />} />
          <Route path="/systems" element={<Systems />} />
          <Route path="/layers" element={<Layers />} />
          <Route path="/overlays" element={<Overlays />} />
          <Route path="/about" element={<About />} />
          <Route path="/info" element={<Info />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/asset/:systemCode/:assetId" element={<AssetPage />} />
          <Route path="/csv-download" element={<CsvDownloadPage />} />


          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </Box>
    </Box>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <CssBaseline />
      <Router>
        <SessionProvider>
          <RealtimeToastProvider>
            <AppContent />
          </RealtimeToastProvider>
        </SessionProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;
